import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  createPrintJob,
  executePrint,
  listPrintJobs,
  printJobPdfUrl,
} from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { ApiError } from "@/api/client";
import type { PrintJobDto } from "@/api/types";

export default function PrintJobPage() {
  const [printMethod, setPrintMethod] = useState<"browser_pdf" | "direct">("browser_pdf");
  const [error, setError] = useState<string | null>(null);
  // 2026-06-29 追加: ピッキングラベル 2 ページ以上の注文があった場合の警告 banner.
  // 手動で閉じる (自動消滅しない=見逃し防止)。
  const [reprintOrderNos, setReprintOrderNos] = useState<string[]>([]);
  const [multiPageOrders, setMultiPageOrders] = useState<
    Array<{ order_no: string; page_count: number; sku_count: number }>
  >([]);
  const queryClient = useQueryClient();
  const credentials = useAuthStore((s) => s.credentials);

  const jobsQ = useQuery({
    queryKey: ["printJobs"],
    queryFn: () => listPrintJobs(20),
  });

  const createMut = useMutation({
    mutationFn: () => createPrintJob({ print_method: printMethod }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["printJobs"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setError(null);
      setMultiPageOrders(data.multi_page_orders ?? []);
      setReprintOrderNos(data.reprint_order_nos ?? []);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const printMut = useMutation({
    mutationFn: (id: string) => executePrint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["printJobs"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  // 認証付き PDF をブラウザで開く: 一度 fetch して Blob URL に変換
  const openPdf = async (id: string) => {
    try {
      const resp = await fetch(printJobPdfUrl(id), {
        headers: credentials ? { Authorization: `Basic ${credentials}` } : {},
      });
      if (!resp.ok) {
        setError(`PDF 取得失敗: HTTP ${resp.status}`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // 一定時間後に revoke (ブラウザが読み込み終わるのを待つ)
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-primary">印刷実行</h2>
        <p className="text-sm text-gray-500 mt-1">
          引き当て済み (allocated) の注文について、出荷ラベル + ピッキングラベルを連続印刷します。
        </p>
      </div>

      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">新規印刷ジョブ</h3>
        <div>
          <label className="label">印刷方法</label>
          <div className="flex gap-3">
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                name="print_method"
                value="browser_pdf"
                checked={printMethod === "browser_pdf"}
                onChange={() => setPrintMethod("browser_pdf")}
                className="mr-2"
              />
              ブラウザ PDF（推奨）
            </label>
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                name="print_method"
                value="direct"
                checked={printMethod === "direct"}
                onChange={() => setPrintMethod("direct")}
                className="mr-2"
              />
              プリンタ直接（TD-4550DNWB）
            </label>
          </div>
        </div>
        <button
          className="btn-primary"
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
        >
          {createMut.isPending ? "ジョブ作成中…" : "印刷ジョブを作成"}
        </button>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">
            {error}
          </div>
        )}
        {reprintOrderNos.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded">
            <p className="font-bold mb-1">
              ℹ️ 再印刷分 {reprintOrderNos.length} 件をこの PDF に含めました
            </p>
            <p className="text-sm mb-2">
              エアロジで引き当て解除 → 再引き当てされた発送済み注文です。
              出荷ラベルは発行済みのもの、ピッキングラベルは最新ロケーションで印字されます。
            </p>
            <ul className="text-sm font-mono ml-4 space-y-0.5">
              {reprintOrderNos.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
          </div>
        )}

        {multiPageOrders.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-400 text-yellow-900 p-4 rounded">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="font-bold text-base mb-2">
                  ⚠️ 以下 {multiPageOrders.length} 件はピッキングラベルが 2 ページ以上になります
                </p>
                <p className="text-sm mb-2">
                  プリンタの自動カット設定を一時的に変更してから「PDF 開く」で印刷してください。
                </p>
                <ul className="text-sm font-mono space-y-1 ml-4">
                  {multiPageOrders.map((o) => (
                    <li key={o.order_no}>
                      • <span className="font-bold">{o.order_no}</span>
                      {" "}({o.page_count} ページ / {o.sku_count} SKU)
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setMultiPageOrders([])}
                className="text-yellow-900 hover:text-yellow-700 text-xl leading-none px-2"
                aria-label="閉じる"
                title="閉じる"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      <section>
        <h3 className="text-lg font-semibold mb-3">印刷ジョブ履歴</h3>
        <div className="card overflow-x-auto">
          {jobsQ.isLoading && <p className="text-gray-500">読み込み中…</p>}
          {jobsQ.error && (
            <p className="text-red-600">取得失敗: {String(jobsQ.error)}</p>
          )}
          {jobsQ.data && (
            <table className="min-w-full text-sm">
              <thead className="border-b text-left text-gray-500">
                <tr>
                  <th className="py-2 pr-4">日付</th>
                  <th className="py-2 pr-4">ジョブID</th>
                  <th className="py-2 pr-4 text-right">件数</th>
                  <th className="py-2 pr-4">ステータス</th>
                  <th className="py-2 pr-4">方式</th>
                  <th className="py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {jobsQ.data.print_jobs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-400">
                      印刷ジョブはまだありません
                    </td>
                  </tr>
                )}
                {jobsQ.data.print_jobs.map((j: PrintJobDto) => (
                  <tr key={j.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">{j.job_date}</td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {j.id.slice(0, 8)}…
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {j.order_count}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          j.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : j.status === "failed"
                              ? "bg-red-100 text-red-800"
                              : j.status === "printing"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {j.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-xs text-gray-600">
                      {j.print_method ?? "-"}
                    </td>
                    <td className="py-2 flex gap-2">
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => openPdf(j.id)}
                      >
                        PDF 開く
                      </button>
                      <button
                        className="btn-accent text-xs"
                        disabled={printMut.isPending}
                        onClick={() => printMut.mutate(j.id)}
                      >
                        直接印刷
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
